import { useMemo, useState } from "react";
import { Search, TrendingUp, TrendingDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { demoScreenerData } from "@/lib/landing-demo-data";

type TabType = "stocks" | "etf" | "crypto";

const TABS: { value: TabType; label: string }[] = [
  { value: "stocks", label: "Stocks" },
  { value: "etf", label: "ETFs" },
  { value: "crypto", label: "Crypto" },
];

const formatLargeCurrency = (value: number, symbol = "$") => {
  if (value >= 1e12) return `${symbol}${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `${symbol}${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${symbol}${(value / 1e6).toFixed(2)}M`;
  return `${symbol}${value.toLocaleString()}`;
};

const TAB_LABELS: Record<TabType, string> = {
  stocks: "Stock",
  etf: "ETF",
  crypto: "Crypto",
};

function resolveLogoUrl(url?: string): string | null {
  if (!url) return null;
  if (!url.includes("img.logo.dev")) return url;

  try {
    const parsed = new URL(url);
    const token = import.meta.env?.VITE_LOGO_DEV_TOKEN as string | undefined;
    if (token && !parsed.searchParams.has("token")) {
      parsed.searchParams.set("token", token);
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

function AssetAvatar({
  symbol,
  name,
  logo,
}: {
  symbol: string;
  name: string;
  logo?: string;
}) {
  const [hasError, setHasError] = useState(false);
  const logoUrl = useMemo(() => resolveLogoUrl(logo), [logo]);

  if (!logoUrl || hasError) {
    return (
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm border border-border/50 shrink-0">
        {symbol.slice(0, 2)}
      </div>
    );
  }

  return (
    <img
      src={logoUrl}
      alt={`${name} logo`}
      className="h-10 w-10 rounded-full border border-border/50 bg-background object-contain p-1 shrink-0"
      loading="lazy"
      decoding="async"
      onError={() => setHasError(true)}
    />
  );
}

export default function ScreenerShowcase() {
  const [activeTab, setActiveTab] = useState<TabType>("stocks");
  const filteredAssets = demoScreenerData.filter((asset) => asset.type === activeTab);

  return (
    <section className="w-full py-20 bg-muted/30">
      <div className="container px-4 md:px-6 mx-auto">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center space-y-4 mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest">
            <Search className="w-3 h-3" />
            Stock Screener
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight font-serif">
            Discover New{" "}
            <span className="text-primary">Investment Opportunities</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Filter thousands of stocks, ETFs, and cryptocurrencies by fundamentals 
            to find your next winning trade.
          </p>
        </div>

        {/* Screener Card */}
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl border border-border/50 bg-card shadow-xl overflow-hidden">
            {/* Header */}
            <div className="p-4 md:p-6 border-b border-border/50">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Tabs */}
                <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
                  {TABS.map((tab) => (
                    <Button
                      key={tab.value}
                      variant={activeTab === tab.value ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setActiveTab(tab.value)}
                      className={cn(
                        "px-4",
                        activeTab !== tab.value && "hover:bg-background/50"
                      )}
                    >
                      {tab.label}
                    </Button>
                  ))}
                </div>

                {/* Search placeholder */}
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <div className="pl-9 pr-4 py-2 rounded-lg border border-border bg-background text-sm text-muted-foreground">
                    Search assets...
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50 bg-muted/30">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      #
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Company
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                      Sector
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Day Change
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden sm:table-cell">
                      Market Cap
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">
                      P/E
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">
                      Div Yield
                    </th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssets.map((asset, index) => {
                    const isPositive = asset.change >= 0;
                    return (
                      <tr
                        key={asset.symbol}
                        className="border-b border-border/30 hover:bg-muted/20 transition-colors cursor-pointer group"
                      >
                        <td className="py-4 px-4 text-sm text-muted-foreground font-medium">
                          {index + 1}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <AssetAvatar
                              symbol={asset.symbol}
                              name={asset.name}
                              logo={asset.logo}
                            />
                            <div>
                              <div className="font-semibold text-sm flex items-center gap-2">
                                {asset.symbol}
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal hidden sm:inline-flex">
                                  {TAB_LABELS[asset.type as TabType]}
                                </Badge>
                              </div>
                              <div className="text-xs text-muted-foreground truncate max-w-[150px]">
                                {asset.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 hidden md:table-cell">
                          <Badge variant="outline" className="font-normal text-xs">
                            {asset.sector}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div
                            className={cn(
                              "flex items-center justify-end gap-1 font-medium text-sm",
                              isPositive ? "text-emerald-600" : "text-red-500"
                            )}
                          >
                            {isPositive ? (
                              <TrendingUp className="h-3 w-3" />
                            ) : (
                              <TrendingDown className="h-3 w-3" />
                            )}
                            {isPositive ? "+" : ""}
                            {asset.change.toFixed(2)}%
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right font-medium text-sm hidden sm:table-cell">
                          {formatLargeCurrency(asset.marketCap, asset.currency)}
                        </td>
                        <td className="py-4 px-4 text-right font-medium text-sm hidden lg:table-cell">
                          {asset.pe !== null ? asset.pe.toFixed(1) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-right font-medium text-sm hidden lg:table-cell">
                          {asset.divYield ? (
                            <span className="text-emerald-600">
                              {(asset.divYield * 100).toFixed(2)}%
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border/50 bg-muted/20">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {filteredAssets.length} of 10,000+ assets
                </p>
                <Button variant="outline" size="sm" className="gap-2" asChild>
                  <a href="/login">
                    View All
                    <ArrowRight className="h-3 w-3" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
